
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { runTestCall } from "@/services/admin/testCallService";
import { fetchAgents } from "@/services/admin/agentService";
import { fetchAvailableAgents } from "@/services/ai/elevenLabsService";
import { TestCallInput } from "@/types/admin";

// Import refactored components
import AgentSourceToggle from "./test-call/AgentSourceToggle";
import ApiErrorAlert from "./test-call/ApiErrorAlert";
import TestCallForm from "./test-call/TestCallForm";
import TestCallResults from "./test-call/TestCallResults";

const TestCallRunner = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [agentSource, setAgentSource] = useState<"local" | "elevenlabs">("elevenlabs");

  // Fetch local DB agents
  const { 
    data: localAgents, 
    isLoading: isLoadingLocalAgents,
    refetch: refetchLocalAgents
  } = useQuery({
    queryKey: ["local-agents"],
    queryFn: fetchAgents,
  });

  // Fetch ElevenLabs agents
  const {
    data: elevenLabsAgentsResponse,
    isLoading: isLoadingElevenLabsAgents,
    refetch: refetchElevenLabsAgents,
    error: elevenLabsError
  } = useQuery({
    queryKey: ["elevenlabs-agents"],
    queryFn: fetchAvailableAgents,
  });

  const elevenLabsAgents = elevenLabsAgentsResponse?.agents || [];
  const showNoAgentsAvailable = !isLoadingElevenLabsAgents && !isLoadingLocalAgents && 
    (agentSource === "elevenlabs" ? elevenLabsAgents.length === 0 : (localAgents?.length || 0) === 0);

  const handleSubmit = async (values: TestCallInput) => {
    try {
      setIsSubmitting(true);
      setTestResult(null);
      
      console.log("Running test call with:", values);

      // Validate required variables
      if (!values.variables || Object.keys(values.variables).length === 0) {
        toast({
          title: "Missing variables",
          description: "Please fill in the required variables",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const requiredVars = ['lead_first_name', 'agent_name', 'dealership_name'];
      const missingVars = requiredVars.filter(v => !values.variables?.[v]);
      
      if (missingVars.length > 0) {
        toast({
          title: "Missing required variables",
          description: `Please fill in all required variables: ${missingVars.join(', ')}`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const result = await runTestCall(values);
      setTestResult(result);

      toast({
        title: "Test call initiated",
        description: "Check your phone for the incoming call"
      });
    } catch (error) {
      console.error("Error initiating test call:", error);
      toast({
        title: "Error running test call",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    refetchElevenLabsAgents();
    refetchLocalAgents();
    toast({
      title: "Refreshing agents list",
      description: "Fetching the latest agents from ElevenLabs and local database",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <AgentSourceToggle 
          agentSource={agentSource} 
          setAgentSource={setAgentSource} 
        />
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={isLoadingElevenLabsAgents || isLoadingLocalAgents}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {elevenLabsError && agentSource === "elevenlabs" && (
        <ApiErrorAlert error={elevenLabsError} />
      )}

      <TestCallForm
        agentSource={agentSource}
        isLoadingElevenLabsAgents={isLoadingElevenLabsAgents}
        isLoadingLocalAgents={isLoadingLocalAgents}
        elevenLabsAgents={elevenLabsAgents}
        localAgents={localAgents}
        isSubmitting={isSubmitting}
        showNoAgentsAvailable={showNoAgentsAvailable}
        onSubmit={handleSubmit}
      />

      <TestCallResults testResult={testResult} />
    </div>
  );
};

export default TestCallRunner;
